import React, { type FC, memo, useCallback, useEffect, useRef, useState } from "react";
import { Button, cn, Description, Label, Radio, RadioGroup } from "@heroui/react";

import type { AgentPanelProps } from "./agentPanels";
import type { AgentMode } from "./runner/types";
import { type MessageId, useTranslation } from "@domain/intl/useTranslation";

interface Option {
    value: AgentMode;
    label: MessageId;
    badge: MessageId;
    desc: MessageId;
}

const options: Option[] = [
    {
        value: "approval",
        label: "app.agent.mode.approval",
        badge: "app.agent.mode.approval.badge",
        desc: "app.agent.mode.approval.desc",
    },
    {
        value: "quick",
        label: "app.agent.mode.quick",
        badge: "app.agent.mode.quick.badge",
        desc: "app.agent.mode.quick.desc",
    },
];

const ModeChoiceAgentPanelImpl: FC<AgentPanelProps> = ({ panel, onDone }) => {
    const { t } = useTranslation();
    const initial = panel.type === "modeChoice" ? panel.initial : "approval";
    const onChosen = panel.type === "modeChoice" ? panel.onChosen : undefined;
    const [mode, setMode] = useState<AgentMode>(initial);
    const continueRef = useRef<HTMLButtonElement>(null);

    useEffect(() => continueRef.current?.focus(), []);

    const handleChange = useCallback((value: string) => setMode(value === "quick" ? "quick" : "approval"), []);
    const handleContinue = useCallback(() => {
        onChosen?.(mode);
        onDone("done");
    }, [onChosen, onDone, mode]);

    return (
        <div className="flex flex-col gap-2" data-testid="agent-mode-choice">
            <div className="font-semibold">{t("app.agent.mode.question")}</div>
            <RadioGroup aria-label={t("app.agent.mode.group")} value={mode} onChange={handleChange}>
                {options.map((option) => (
                    <Radio key={option.value} value={option.value}>
                        <Radio.Content
                            className={cn(
                                "w-full flex-row items-start gap-2 rounded-xl border border-separator bg-surface px-3 py-2",
                                mode === option.value && "border-accent",
                            )}
                        >
                            <Radio.Control className="mt-1 border">
                                <Radio.Indicator />
                            </Radio.Control>
                            <div className="flex flex-col">
                                <Label>
                                    {t(option.label)}
                                    <span className="ml-2 rounded-full bg-accent/15 px-2 py-0.5 text-xs text-accent">
                                        {t(option.badge)}
                                    </span>
                                </Label>
                                <Description>{t(option.desc)}</Description>
                            </div>
                        </Radio.Content>
                    </Radio>
                ))}
            </RadioGroup>
            <div>
                <Button ref={continueRef} variant="primary" onPress={handleContinue}>
                    {t("app.agent.mode.continue")}
                </Button>
            </div>
        </div>
    );
};

export const ModeChoiceAgentPanel = memo(ModeChoiceAgentPanelImpl);
